import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

/** Same key AuthContext persists sessions under — read only to know whether
 *  there is a session worth waiting on. */
function hasStoredSession(): boolean {
  try {
    const raw = localStorage.getItem('sessions');
    return raw != null && raw !== '' && raw !== '[]';
  } catch {
    return false;
  }
}

interface OpenRouteGateProps {
  children: ReactNode;
  /** Rendered while a stored session is still being validated. */
  fallback: ReactNode;
}

/**
 * Guards every `/what-we-do/sat/*` open route (specs/009 L1 + Router).
 *
 * The open surface is the anonymous product; a signed-in SAT student has a
 * real course at `/sat`, and playing anonymously as one would write progress
 * to a local store that never reaches their account. So a `STUDENT` with
 * `satEnabled` is sent to the course home from the course home AND from the
 * player / result routes (a bookmarked or back-button URL).
 *
 * Auth restores asynchronously. Waiting on it for everyone would flash a
 * loader on every anonymous visit and corrupt the prerendered empty state, so
 * the wait applies only when the browser holds a stored session to validate —
 * exactly the case where the answer could differ.
 */
export function OpenRouteGate({ children, fallback }: OpenRouteGateProps) {
  const { user, isLoading } = useAuth();

  if (isLoading && hasStoredSession()) return <>{fallback}</>;
  if (user?.role === 'STUDENT' && user.satEnabled) return <Navigate to="/sat" replace />;
  return <>{children}</>;
}
